import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { ROUTES } from '@constants/routes';
import { SeriesTableRow } from '@containers/gridMode/SeriesTableRow';
import { AddPromptCell } from '@containers/gridMode/AddPromptCell';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { streamTableResults } from '@hooks/features/useTableResults';
import { Working } from '@ui/Working';
import { useDispatch, useSelector } from 'react-redux';
import { FlatButton } from '@ui/buttons';
import { convertTableToCSV, downloadCSV } from '@utils/csvExport';

const Wrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;
const ExportButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: 24px;
`;
const IncompleteMessage = styled.div`
  padding-bottom: 8px;
`;
const TableWrapper = styled.table`
  width: 100%;
  margin-top: 24px;
  border-collapse: separate;
  border-spacing: 0;

  th {
    // position: sticky;
    top: 0;
    z-index: 3;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  /* First column (question column) sticky */
  th:first-child,
  td:first-child {
    // position: sticky;
    left: 0;
    z-index: 2;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  /* First cell (top-left corner) highest z-index */
  th:first-child {
    z-index: 4;
  }

  .question-column {
    min-width: 299px;
    max-width: 400px;
  }

  th,
  td {
    vertical-align: top;
    padding: 16px;
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  td {
    min-width: 600px;
  }

  /* Ensure sticky columns have proper borders */
  th:first-child,
  td:first-child {
    min-width: 300px;
    border-right: 2px solid ${({ theme }) => theme.themeColors.border};
  }
`;
const DocLink = styled.tr`
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

function DocHeading({ data: docs }) {
  const onViewOriginalDoc = (doc) => {
    const { document_url, id } = doc;

    if (document_url?.length > 0) {
      window.open(document_url, '_blank');
    } else {
      window.open(ROUTES.docViewer + `?id=${id}`, '_blank');
    }
  };

  if (!docs) {
    return null;
  }

  return (
    <th>
      {docs.map((doc) => (
        <DocLink key={doc.id} onClick={() => onViewOriginalDoc(doc)}>
          <Body5 bold>{doc.filename}</Body5>
        </DocLink>
      ))}
    </th>
  );
}

const generateTableRowsResultsFromCells = (tableRows, cells) => {
  const rows = {};
  cells.forEach((c) => {
    const tableRow = tableRows[c.rowIndex];
    const row = rows[c.rowIndex];
    if (!row) {
      const data = {
        ...tableRow,
        cells: [c],
      };
      rows[c.rowIndex] = data;
    } else {
      row.cells.push(c);
    }
  });
  return Object.values(rows);
};

const getCellResultId = ({ groupResultId, rowIndex, cellIndex }) => `${groupResultId}_${rowIndex}_${cellIndex}`;

const shouldHideCellAnswer = (cell, index, cells) => index === cells.length - 1 && cell.isDiffModeRow;

const buildQueryArrayResultIds = (result) => {
  const childResultIds = (result.data?.queries || []).map((q) => q.query_run_id);
  return {
    groupResultId: result.runId,
    childResultIds,
  };
};

const generateQuestionList = (currentTableRows, prevTableRows) => {
  const questionList = [];
  const prevLength = prevTableRows.length;
  currentTableRows.forEach((row, rowIndex) => {
    const isDiffRow = row.prompt.is_diff_mode;

    if (rowIndex >= prevLength) {
      const rowCellDocuments = row.documents.filter((d) => !!d);

      rowCellDocuments.forEach((documents, cellIndex) => {
        const question = row.prompt;
        let doc_list = documents.map((d) => d.id);
        if (isDiffRow) {
          const nextCell = rowCellDocuments[cellIndex + 1];
          if (nextCell) {
            const nextCellDocList = nextCell.map((d) => d.id);
            doc_list = [...doc_list, ...nextCellDocList];
          }
        }
        const questionItem = {
          question: question.prompt,
          doc_list,
          sourceDocs: documents,
          rowIndex,
          cellIndex,
          isDiffModeRow: !!isDiffRow,
        };
        questionList.push(questionItem);
      });
    }
  });
  return questionList;
};

export const SeriesTable = ({ tableRows, docColumns, tool, docMode, selectedAgent }) => {
  const dispatch = useDispatch();
  const advancedSettings = useAdvancedSettings();
  const resultLookup = useSelector((state) => state.gridTableResults.resultLookup);
  const completedResultsLookup = useSelector((state) => state.gridTableResults.completedResultsLookup);
  const isAgentResult = !!selectedAgent;

  const prevTableRows = React.useRef([]);
  const [tableRowsResults, setTableRowsResults] = React.useState([]);
  const [agentResultLookup, setAgentResultLookup] = React.useState({});
  const [completedAgentResultsLookup, setCompletedAgentResultsLookup] = React.useState({});
  const [generatingRowResults, setGeneratingRowResults] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [showIncompleteMessage, setShowIncompleteMessage] = React.useState(false);
  const activeStreams = React.useRef([]);

  const handleAgentCellComplete = React.useCallback((cell, result) => {
    const cellResultId = getCellResultId(cell);
    setAgentResultLookup((prevState) => ({
      ...prevState,
      [cellResultId]: result,
    }));
    setCompletedAgentResultsLookup((prevState) => ({
      ...prevState,
      [cellResultId]: true,
    }));
  }, []);

  React.useEffect(() => {
    const getTableResults = async (prevTableRows, currentTableRows) => {
      const columnLength = currentTableRows[0].documents.filter((d) => !!d).length;
      setGeneratingRowResults(true);
      const { max_context_tokens } = advancedSettings;
      const questionList = generateQuestionList(currentTableRows, prevTableRows);
      if (questionList.length === 0) {
        setGeneratingRowResults(false);
        return;
      }
      const v2Questions = questionList.map((q) => ({ question: q.question, doc_ids: q.doc_list }));
      const settings = {
        user_mode: tool,
        include_context: true,
        max_context_tokens: isNaN(max_context_tokens) ? 32000 : parseInt(max_context_tokens, 10),
      };
      addAdvancedSettingValues(advancedSettings, settings, ['max_context_tokens']);
      const result = await submitQueriesStream({
        questions: v2Questions,
        agentName: selectedAgent || undefined,
        settings,
      });
      setGeneratingRowResults(false);
      if (result.status === 200) {
        const { groupResultId, childResultIds } = buildQueryArrayResultIds(result);
        const cells = questionList.map((q, i) => ({
          ...q,
          groupResultId,
          childResultId: childResultIds[i],
        }));
        setTableRowsResults((prevState) => {
          const prevTableRowsLength = prevState.length;
          if (!isAgentResult) {
            const stream = streamTableResults(groupResultId, dispatch, prevTableRowsLength, columnLength);
            activeStreams.current.push(stream);
          }
          return [...prevState, ...generateTableRowsResultsFromCells(currentTableRows, cells)];
        });
      } else {
        setTimeout(() => {
          getTableResults(prevTableRows, currentTableRows);
        }, 2000);
      }
    };

    if (prevTableRows.current.length !== tableRows.length) {
      getTableResults(prevTableRows.current, tableRows);
      prevTableRows.current = tableRows;
    }

    return () => {
      // Clean up all active streams on unmount
      activeStreams.current.forEach((stream) => {
        if (stream && stream.cancel) {
          stream.cancel();
        }
      });
      activeStreams.current = [];
    };
  }, [advancedSettings, dispatch, selectedAgent, tableRows]);

  // Check if all results have finished streaming
  const areAllResultsComplete = () => {
    if (tableRowsResults.length === 0) return false;

    if (isAgentResult) {
      return tableRowsResults.every((row) =>
        row.cells.every((cell, index) => {
          if (shouldHideCellAnswer(cell, index, row.cells)) {
            return true;
          }
          return !!completedAgentResultsLookup[getCellResultId(cell)];
        })
      );
    }

    const allGroupResultIds = new Set(tableRowsResults.flatMap((row) => row.cells.map((cell) => cell.groupResultId)));

    for (const groupResultId of allGroupResultIds) {
      if (!completedResultsLookup[groupResultId]) {
        return false;
      }
    }

    return true;
  };

  const handleExportToCSV = () => {
    if (!areAllResultsComplete()) {
      setShowIncompleteMessage(true);
      const showTime = 5000;
      setTimeout(() => setShowIncompleteMessage(false), showTime);
      return;
    }

    try {
      setIsExporting(true);

      // Prepare table data with results from Redux store
      const tableDataWithAnswers = tableRowsResults.map((row) => {
        const rowWithAnswers = { ...row };
        rowWithAnswers.cells = row.cells.map((cell, index) => {
          const cellResultId = getCellResultId(cell);
          let answer = '';
          if (!shouldHideCellAnswer(cell, index, row.cells)) {
            answer = isAgentResult ? agentResultLookup[cellResultId] || '' : resultLookup[cellResultId] || '';
          }
          return {
            ...cell,
            answer,
          };
        });
        return rowWithAnswers;
      });

      // Convert to CSV
      const csvContent = convertTableToCSV(tableDataWithAnswers, docColumns);

      // Download the CSV
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `table-export-${timestamp}.csv`;
      downloadCSV(csvContent, filename);

      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      setIsExporting(false);
    }
  };

  return (
    <Wrapper>
      <TableWrapper>
        <thead>
          <th className={'question-column'}>
            <Body5 bold>Question</Body5>
          </th>
          {docColumns.map((d, i) => (
            <DocHeading key={i} data={d} index={i} />
          ))}
        </thead>
        <tbody>
          {tableRowsResults.map((r, i) => (
            <SeriesTableRow
              key={i}
              index={i}
              data={r}
              tool={tool}
              docMode={docMode}
              isAgentResult={isAgentResult}
              onAgentCellComplete={handleAgentCellComplete}
            />
          ))}
          {generatingRowResults ? (
            <tr>
              <td>
                <Working />
              </td>
            </tr>
          ) : null}

          <tr>
            <td>
              <AddPromptCell />
            </td>
            <td colSpan={docColumns.length - 1}></td>
          </tr>
        </tbody>
      </TableWrapper>
      {tableRowsResults.length > 0 && (
        <ExportButtonWrapper>
          {showIncompleteMessage && (
            <IncompleteMessage>
              <Body5>Please wait for all answers to complete before exporting</Body5>
            </IncompleteMessage>
          )}
          <FlatButton onClick={handleExportToCSV} disabled={isExporting || generatingRowResults}>
            {isExporting ? 'Exporting...' : 'Export to CSV'}
          </FlatButton>
        </ExportButtonWrapper>
      )}
    </Wrapper>
  );
};
